import React, {useEffect, useRef, useState} from 'react';
import {Box, InputAdornment, List, ListItem, ListItemIcon, ListItemText, Paper, TextField} from '@mui/material';
import {FireTruckOutlined, PersonOutlined, Search as SearchIcon} from '@mui/icons-material';
import {Link} from 'react-router-dom';
import Typography from "@mui/material/Typography";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";

// Define a constant for the max height of the results
const MAX_RESULTS_HEIGHT = 225; // You can adjust this value as needed

const CustomSearch = ({placeholder, resultData, isLoading, onTextInput}: any) => {
    const [inputValue, setInputValue] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const [filteredType, setFilteredType] = useState('all');
    const searchRef: any = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event: any) => {
            if (searchRef.current && !searchRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleInputChange = (event: any) => {
        const value = event.target.value;
        setInputValue(value);
        onTextInput(event, value);
        setIsOpen(value.length > 0);
    };

    const getCounts = () => {
        const counts: any = {all: 0, customers: 0, orders: 0, locations: 0, classes: 0};
        resultData.forEach((item: any) => {
            counts[item.type]++;
            counts.all++;
        });
        return counts;
    };

    const counts = getCounts();

    const CustomChip = ({icon, label, count, type}: any) => (
        <Box
            onClick={() => setFilteredType(type)}
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '8px 12px',
                border: '1px solid #e0e0e0',
                borderRadius: '4px',
                backgroundColor: filteredType === type ? '#e0e0e0' : '#f5f5f5',
                color: 'inherit',
                cursor: 'pointer',
                '&:hover': {
                    backgroundColor: '#e0e0e0',
                },
            }}
        >
            <Box sx={{display: 'flex', alignItems: 'center'}}>
                {icon ? icon : "All"}
                <Typography
                    variant="body2"
                    sx={{
                        ml: 1,
                        display: {xs: 'none', md: 'block'}  // Hide label on mobile, show on medium and up
                    }}
                >
                    {label}
                </Typography>
            </Box>
            <Typography
                variant="body2"
                sx={{
                    ml: 2,
                    fontWeight: 'bold',
                }}
            >
                {count}
            </Typography>
        </Box>
    );


    const renderCategoryChips = () => (
        <Box sx={{
            display: 'flex',
            gap: 1,
            mb: 2,
            flexDirection: {xs: 'row', md: 'column'}, // row for small screens, column for medium and up
            flexWrap: {xs: 'wrap', md: 'nowrap'}, // allow wrapping on small screens
            justifyContent: {xs: 'center', md: 'flex-start'}, // center items on small screens
            width: {xs: '100%', md: 200}, // 100% width on small screens, fixed width on medium and up
        }}>
            <CustomChip count={counts.all} type="all"/>
            <CustomChip icon={<PersonOutlined fontSize="small"/>} label="Customers" count={counts.customers}
                        type="customers"/>
            <CustomChip icon={<DescriptionOutlinedIcon fontSize="small"/>} label="Orders" count={counts.orders}
                        type="orders"/>
            <CustomChip icon={<LocationOnOutlinedIcon fontSize="small"/>} label="Locations" count={counts.locations}
                        type="locations"/>
            <CustomChip icon={<FireTruckOutlined fontSize="small"/>} label="Trucks" count={counts.classes}
                        type="classes"/>
        </Box>
    );

    const renderResults = () => {
        const filteredResults = filteredType === 'all'
            ? resultData
            : resultData.filter((item: any) => item.type === filteredType);

        return (
            <Box sx={{
                maxHeight: MAX_RESULTS_HEIGHT,
                overflowY: 'auto',
                display: 'flex',
                flexGrow: 1,
                '&::-webkit-scrollbar': {
                    width: '0.4em'
                },
                '&::-webkit-scrollbar-track': {
                    boxShadow: 'inset 0 0 6px rgba(0,0,0,0.00)',
                    webkitBoxShadow: 'inset 0 0 6px rgba(0,0,0,0.00)'
                },
                '&::-webkit-scrollbar-thumb': {
                    backgroundColor: 'rgba(0,0,0,.1)',
                    outline: '1px solid slategrey'
                }
            }}>
                <List>
                    {filteredResults.map((item: any, index: any) => (
                        <ListItem
                            key={index}
                            component={Link}
                            to={item.type === 'classes' ? `/admin/trucks/${item.id}` : `/admin/${item.type}/${item.id}`}
                            sx={{
                                gap: 1,
                                display: 'flex',
                                flexDirection: 'row'
                            }}
                        >
                            <ListItemIcon sx={{minWidth: 36}}>
                                {item.type === 'customers' && <PersonOutlined/>}
                                {item.type === 'orders' && <DescriptionOutlinedIcon/>}
                                {item.type === 'locations' && <LocationOnOutlinedIcon/>}
                                {item.type === 'classes' && <FireTruckOutlined/>}
                            </ListItemIcon>
                            <ListItemText
                                primary={item.type === 'orders' ? `Order ${item.order_number}` : item.name}
                                secondary={item.email || item.firstname}
                                sx={{marginLeft: 0, display: 'flex', flexDirection: 'row', gap: 3}}
                                primaryTypographyProps={{fontWeight: 'bold'}} // This makes the primary text bold
                            />
                        </ListItem>
                    ))}
                </List>
            </Box>
        );
    };

    return (
        <Box ref={searchRef} sx={{position: 'relative', width: '100%'}}>
            <TextField
                fullWidth
                placeholder={placeholder}
                value={inputValue}
                onChange={handleInputChange}
                onFocus={() => setIsOpen(true)}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon/>
                        </InputAdornment>
                    ),
                }}
            />
            {isOpen && (
                <Paper sx={{
                    position: 'absolute',
                    width: '100%',
                    mt: 1,
                    zIndex: 1,
                    display: 'flex',
                    flexDirection: {xs: 'column', md: 'row'}, // Column layout on mobile, row on larger screens
                    padding: 2
                }}>
                    {renderCategoryChips()}
                    {renderResults()}
                </Paper>
            )}
        </Box>
    );
};

export default CustomSearch;
